import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

type Mode = 'signin' | 'signup'

export function LoginScreen() {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({ email, password })
        if (error) throw error
        // If email confirmation is OFF, a session is returned immediately and
        // the auth listener in App swaps to the board. If it's ON, no session.
        if (!data.session) {
          setNotice(
            'Compte créé. Vérifiez vos emails pour confirmer, puis connectez-vous.',
          )
          setMode('signin')
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <img
            src="/favicon.svg"
            alt="msgban"
            className="h-14 w-14 rounded-2xl shadow-[var(--shadow-soft)]"
          />
          <div>
            <div className="text-[18px] font-semibold text-[var(--color-text-strong)]">
              msgban
            </div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--color-text-dim)]">
              Chat → Workflow
            </div>
          </div>
        </div>

        {/* Card */}
        <form
          onSubmit={submit}
          className="rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-modal)]"
        >
          <h1 className="mb-1 text-[15px] font-semibold text-[var(--color-text-strong)]">
            {mode === 'signin' ? 'Connexion' : 'Créer un compte'}
          </h1>
          <p className="mb-5 text-[12px] text-[var(--color-text-dim)]">
            {mode === 'signin'
              ? 'Accédez à vos canaux et tickets.'
              : 'Quelques secondes pour démarrer.'}
          </p>

          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-[var(--color-text-dim)]">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] focus:bg-white"
            />
          </label>

          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-medium text-[var(--color-text-dim)]">
              Mot de passe
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] focus:bg-white"
            />
          </label>

          {error && (
            <div className="mt-3 rounded-xl border border-red-200 bg-[var(--color-danger-bg)] px-3 py-2 text-[11.5px] text-[var(--color-danger)]">
              {error}
            </div>
          )}
          {notice && (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-[var(--color-done-bg)] px-3 py-2 text-[11.5px] text-emerald-800">
              {notice}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-[var(--color-accent-hover)] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'signin' ? 'Se connecter' : 'Créer le compte'}
          </button>

          <button
            type="button"
            onClick={() => {
              setMode((m) => (m === 'signin' ? 'signup' : 'signin'))
              setError(null)
              setNotice(null)
            }}
            className={cn(
              'mt-3 block w-full text-center text-[12px] text-[var(--color-text-dim)]',
              'hover:text-[var(--color-text)]',
            )}
          >
            {mode === 'signin'
              ? "Pas encore de compte ? En créer un"
              : 'Déjà un compte ? Se connecter'}
          </button>
        </form>
      </div>
    </div>
  )
}
